import React, { useEffect, useMemo, useRef } from 'react';

import { gql, useQuery } from '@apollo/client';
import { Button } from 'ui-kit';

import {
  AssetClass,
  FileState,
  Filter,
  FilterableField,
  FilterOperator,
} from '__generated__/globalTypes';
import { GetTapeLoanCount, GetTapeLoanCountVariables } from 'query/__generated__/getTapeLoanCount';
import { GET_TAPE_LOAN_COUNT_QUERY } from 'query/getTapeLoanCount';

import { writePoolCandidate } from './sessionPoolCandidate';
import { formatLoanCount, pluralizeLoans } from './tapePoolNaming';

export const PoolFromTapeButtonFragments = {
  loanTape: gql`
    fragment PoolFromTapeButtonFile on File {
      id
      state
      name
      file_name
      asset_class
    }
  `,
};

type SelectedFile = {
  id: string;
  state: FileState;
  name: string | null;
  file_name: string;
  asset_class: AssetClass;
};

type Props = {
  selectedFile: SelectedFile;
  /**
   * Fires when the user clicks the button. Caller is expected to (a) close
   * any enclosing modal/overlay so the pool modal isn't stacked on top of
   * a higher-z parent, and (b) open the controlled `PoolFromTapeModal`.
   */
  onPoolFromTape: () => void;
  /**
   * Treat the tape as persisted regardless of `File.state`. The Tapestry
   * outcome panel passes this on a `success` completion_outcome — that outcome
   * is the pipeline's authoritative "loans persisted" signal, and `File.state`
   * lags it (the 5s GetLoanTape poll trails the job's terminal outcome, and may
   * not transition to PERSISTED at all for a pipeline run). Without it the pool
   * CTA would be hidden after a successful Tapestry persist.
   */
  persistedOverride?: boolean;
};

const PoolFromTapeButton = ({
  selectedFile,
  onPoolFromTape,
  persistedOverride = false,
}: Props): JSX.Element | null => {
  const writtenForRef = useRef<string | null>(null);

  const tapeName = selectedFile.name ?? selectedFile.file_name;
  const isPersisted = persistedOverride || selectedFile.state === FileState.PERSISTED;

  useEffect(() => {
    if (!isPersisted) return;
    if (writtenForRef.current === selectedFile.id) return;
    writePoolCandidate({
      tapeId: selectedFile.id,
      tapeName,
      assetClass: selectedFile.asset_class,
      persistedAt: Date.now(),
    });
    writtenForRef.current = selectedFile.id;
  }, [isPersisted, selectedFile.id, selectedFile.asset_class, tapeName]);

  const tapeFilter: Filter[] = useMemo(
    () => [
      {
        field_name: FilterableField.last_loan_tape_id,
        operator: FilterOperator.IS,
        operand: selectedFile.id,
        operandList: null,
      },
    ],
    [selectedFile.id],
  );

  const {
    data: countData,
    loading: countLoading,
    error: countError,
  } = useQuery<GetTapeLoanCount, GetTapeLoanCountVariables>(GET_TAPE_LOAN_COUNT_QUERY, {
    variables: { filters: tapeFilter },
    skip: !isPersisted,
    fetchPolicy: 'no-cache',
  });

  const loanCount = countData?.user?.company?.performance_summary?.loan_count ?? null;

  if (!isPersisted) return null;
  if (countError) return null;

  // Disable when the count is unknown (null) too — clicking would otherwise
  // fire createPool with a filter that may return zero loans server-side
  // and produce an empty pool. Bugbot caught this on commit 6cfde245.
  const buttonDisabled = countLoading || loanCount === null || loanCount === 0;
  const labelText = (() => {
    if (countLoading) return 'Loading loan count…';
    if (loanCount === null) return 'Pool from this tape';
    if (loanCount === 0) return 'No loans to pool';
    return `Pool from this tape (${formatLoanCount(loanCount)} ${pluralizeLoans(loanCount)})`;
  })();

  return (
    <div data-testid="create-pool-from-tape-cta" className="w-full">
      <Button
        type="primary_dark"
        size="large"
        label="pool from tape"
        disabled={buttonDisabled}
        onClick={onPoolFromTape}
        className="w-full justify-center"
        data-testid="create-pool-from-tape-button"
      >
        {labelText}
      </Button>
    </div>
  );
};

export default PoolFromTapeButton;
